import { SingleReview } from "@/comps/feature/reviews"
import { WrappedFooter } from "@/comps/footer"
import Header from "@/comps/header"
import { ErrorMessage } from "@/comps/message"
import Meta from "@/comps/meta"
import {
	PaymentSessionData,
	PAYMENT_DATA_KEY,
	useAdjustLogger,
} from "@/utils/adjust"
import { assetUrl } from "@/utils/cdn"
import useAuth from "@/utils/client-auth"

import {
	getAdId,
	getCurrentPackId,
} from "@/comps/subscription-popup"
import { axios } from "@/utils/axios-wrapper"
import { NotificationContext } from "@/utils/notification"
import { SSRNotFound, wrapSSRAuth } from "@/utils/ssr-auth"
import { APP_HOST } from "@/utils/variables"
import clsx from "clsx"
import {
	OfferingInfoResponse,
	OfferingPrices,
	PaymentResponse,
	PaymentStripeUrlRequest,
} from "models/general"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { useContext, useState } from "react"
import Compare from "sections/pricing/compare"
import PricingOrCoin from "sections/pricing/pricing-or-coin"
import Question from "sections/pricing/question"
import SubscriptionDetails from "sections/pricing/subscription-details"
import { v4 } from "uuid"

import { getMainContext } from "@/backend/misc/route-handler"
import offeringInfoHandler from "../api/v1/membership/offering-info"
import offeringPricesHandler from "../api/v1/offering/prices"

interface PricingProps {
	prices: OfferingPrices
	plans: OfferingInfoResponse["result"]
}

export default function Pricing(props: PricingProps) {
	const { prices, plans } = props
	const { package_compare } = plans

	const { t } = useTranslation()
	const { userInfo } = useAuth()
	const router = useRouter()
	const { notify } = useContext(NotificationContext)
	const logAdjust = useAdjustLogger()

	const { packId: currentPackId, isYearly: isPackYearly } =
		getCurrentPackId(
			userInfo ? userInfo.entitlements : [],
			prices,
		)

	const [isYearly, setIsMonthly] = useState(isPackYearly)

	const currentValue =
		prices.plans.advanced.variants?.findIndex(
			(a) => a.pack_id === currentPackId,
		) ?? -1

	const [packIndex, setPackIndex] = useState<number>(
		currentValue !== -1 ? currentValue : 0,
	)

	const [packId, setPackId] = useState(
		prices.plans.advanced.variants?.[packIndex].pack_id ??
			"",
	)

	async function handlePurchaseClick() {
		if (userInfo) {
			fetchPaymentLink()
		} else {
			router.push("/login")
		}
	}

	async function fetchPaymentLink() {
		try {
			const sessionId = v4()
			const path = window.location.pathname
			const search = window.location.search

			const parsedUrl =
				location === undefined ? path + search : location
			const period = isYearly ? "yearly" : "monthly"

			const successUrl = new URL(APP_HOST + parsedUrl)
			successUrl.searchParams.delete(period)
			successUrl.searchParams.delete("session")

			successUrl.searchParams.append(period, "success")
			successUrl.searchParams.append("session", sessionId)

			const cancelUrl = new URL(APP_HOST + parsedUrl)
			cancelUrl.searchParams.delete(period)
			cancelUrl.searchParams.append(period, "cancel")

			const payload: PaymentStripeUrlRequest = {
				success_url: successUrl.toString(),
				cancel_url: cancelUrl.toString(),
				period,
				adjust_id: await getAdId(),
				offering: packId,
			}

			const { url, currency, amount_total } = await axios
				.post<PaymentResponse>(
					"/api/v1/payment/request-stripe-url",
					payload,
				)
				.then((res) => res.data.result)

			logAdjust?.logEvent("initiate_checkout")

			const paymentData: PaymentSessionData = {
				currency,
				value: amount_total,
				session: sessionId,
			}

			localStorage.setItem(
				PAYMENT_DATA_KEY,
				JSON.stringify(paymentData),
			)

			router.push(url)
		} catch (error) {
			console.error(error)
			notify(
				<ErrorMessage>
					{t("txt_could_not_purchase_now")}
				</ErrorMessage>,
			)
		}
		window.blur()
	}

	return (
		<>
			<Meta
				title={t("txt_plan_and_pricing")}
				description={t("txt_home_description")}
			/>
			<div className="h-screen w-full">
				<div>
					<Header isStatic />
				</div>

				{userInfo && <SubscriptionDetails />}

				<div
					id="pricing"
					className="flex w-full flex-col items-center justify-center gap-5 pt-[50px] tablet:gap-8 tablet:px-5 desktop:gap-[61px] desktop:pt-[100px]">
					<h1 className="w-[250px] text-center text-[20px] font-700 text-blue-900 tablet:w-[300px] tablet:text-[24px] desktop:w-[400px] desktop:text-[32px]">
						{t("txt_pricing_title")}
					</h1>
				</div>

				<PricingOrCoin
					isYearly={isYearly}
					setIsMonthly={setIsMonthly}
					prices={prices}
					savePercent={prices.save_percent}
					handlePurchaseClick={handlePurchaseClick}
					setPackId={setPackId}
					setPackIndex={setPackIndex}
					packIndex={packIndex}
				/>

				<Compare
					sections={package_compare.sections}
					packages={package_compare.packages}
					isYearly={isYearly}
					prices={prices}
					setIsMonthly={setIsMonthly}
					handlePurchaseClick={handlePurchaseClick}
					packId={packId}
				/>

				<div
					className={clsx(
						"desktop:pb-[40px]",
						"flex w-full items-center justify-center pt-[48px]",
					)}>
					<img
						src={assetUrl("/general/editor-choice.svg")}
						alt="editor choice"
						className="h-[60px] w-[100px]"
					/>
				</div>

				<div
					className={clsx(
						"flex w-full flex-col items-center gap-[32px]",
					)}>
					<span className="text-[24px] font-700">
						{t("txt_we_trust")}
					</span>
					<div
						className={clsx(
							"no-scrollbar flex w-full gap-[20px] overflow-x-scroll",
							"px-[20px] tablet:justify-center desktop:items-center",
						)}>
						{prices.reviews.map((review, index) => {
							return (
								<SingleReview
									review={{
										name: review.name,
										rating: review.stars,
										opinion: review.text,
										avatar: review.image,
									}}
									avatar={review.image}
									key={index}
								/>
							)
						})}
					</div>
				</div>

				<div
					className={clsx(
						"flex w-full flex-col items-center gap-[30px] py-[60px]",
						"desktop:flex-row desktop:items-start desktop:justify-center desktop:gap-[24px]",
						"desktop:pt-[110px]",
					)}>
					<p className="text-h5 desktop:w-[272px]">
						{t("txt_asked_questions")}
					</p>
					<div
						className={clsx(
							"flex w-full flex-col gap-[16px] px-[20px] desktop:w-[775px] desktop:p-0",
							"no-scrollbar max-h-[724px] overflow-y-scroll tablet:items-center",
						)}>
						{prices.faq.map((question, index) => {
							return (
								<Question
									question={question.q}
									answer={question.a}
									key={index}
								/>
							)
						})}
					</div>
				</div>

				<div>
					<WrappedFooter />
				</div>
			</div>
		</>
	)
}

async function getPricesSSR() {
	try {
		const result = await offeringPricesHandler.ssr({
			...getMainContext(),
		})

		return result
	} catch (e) {
		console.error(e)
		return null
	}
}

async function getPlansSSR() {
	try {
		const result = await offeringInfoHandler.ssr({
			...getMainContext(),
		})

		return result.result
	} catch (e) {
		console.error(e)
		return null
	}
}

export const getServerSideProps =
	wrapSSRAuth<PricingProps>(async () => {
		const [prices, plans] = await Promise.all([
			getPricesSSR(),
			getPlansSSR(),
		])

		if (!prices || !plans) {
			throw new SSRNotFound()
		}

		return {
			prices,
			plans,
		}
	}, [])
